import { 
  useContext, 
  useMemo, 
  useCallback, 
  useState, 
  useRef, 
  useEffect 
} from "react";

import { TenantContext } from "../../../../../templates/context/TenantContext";
import { getProductPrediction, getAllPredictionsFromMonth } from "../../../../../utils/utils";
import { forecastColumns } from "../../../../../utils/tableColumns";

import { BodyCells, CheckCell, HierarchyBodyCells } from "../../../sharedComponents/analysisModuleSharedComponents.index"

import { Button, Text } from "@aidron/aidron-ds";

import { i18n } from "../../../../../translate/i18n";

import PropTypes from 'prop-types';
import { ORDERING_MODES, TABLE_TYPE } from "../../../../../components/components.constants";
import { useAnalysisViewModel } from "../../../../../viewModel/useAnalysysViewModel";


function* createProductIdsGenerator(productIds) {
  for (const productId of productIds) {
    yield productId;
  }
}

function AccuracySubtotalRow({ actions, ctx, uiState }) {
  const { t } = i18n;

  const { setCheckedProducts,} = actions || {};

  const { 
    selectedProductIds, 
    defineHierarchyToDisplay, 
    forecastAssertivenessMonths,
    pastPeriodColumns,
    predictionsForAssertivenessAnalysis
  } = ctx || {};

  const { 
    freezeColumns, 
    checkedProducts, 
    evaluateLastCategoryColumn, 
    headerHeight
  } = uiState || {};

  const calculatePrediction = useCallback((month) => {
    const allPredictionsFromMonth = getAllPredictionsFromMonth(month, predictionsForAssertivenessAnalysis);
    const monthProductPredictions = allPredictionsFromMonth.filter(prediction =>
      selectedProductIds.includes(prediction.productId)
    );

    const result = {};

    pastPeriodColumns.forEach(column => {
      const values = { allPredictions: allPredictionsFromMonth, selectedPredictions: monthProductPredictions, ...column };

      result[column.name] = column.calculate(values);
    });
    return result;
  }, [predictionsForAssertivenessAnalysis, selectedProductIds]);

  const defineLeftCellBorder = useCallback((monthIndex, columnIndex) => {
    return columnIndex === 0 && (selectedProductIds?.length !== 0 || monthIndex !== 0);
  }, [selectedProductIds]);

  const forecastColumnsBorders = useMemo(() => {
    return forecastAssertivenessMonths.map((month, index) => forecastColumns().map((_, i) => defineLeftCellBorder(index, i)));
  }, [forecastAssertivenessMonths, defineLeftCellBorder]);
  
  return selectedProductIds.length > 0 ? (
    <tr className="font-weight-bold-on-paragraph">
      <CheckCell
        actions={{
          setCheckedProducts: setCheckedProducts
        }}
        ctx={{
          checkedProducts: checkedProducts,
          productIds: selectedProductIds
        }}
        uiState={{
          freezeColumns: freezeColumns,
          headerHeight: headerHeight
        }}/>

      {defineHierarchyToDisplay().length !== 0 ? (
        <td
          colSpan={defineHierarchyToDisplay().length}
          className={`background-color-white padding-sides-040rem z-2 cell-bottom-border position-sticky
          ${evaluateLastCategoryColumn(defineHierarchyToDisplay().length - 1)} ${freezeColumns ? "" : "cell-right-border"}
          `}
          style={{
            left: freezeColumns ? `30px` : "",
            top: `${headerHeight}px`,
          }}
        >
          <Text 
           className="font-size-17px padd-text">{t("total")}</Text>
        </td>
      ) : (
        <></>
      )}

      {forecastAssertivenessMonths.map((month, index) => (
        <BodyCells
          ctx={{
            columns: pastPeriodColumns,
            prediction: calculatePrediction(month),
            productIds: selectedProductIds,
            monthIndex: index,
            table: TABLE_TYPE.ACCURACY
          }}
          uiState={{
            headerHeight: headerHeight,
            forecastColumnsBorders: forecastColumnsBorders[index],
            cellTopBorder: true
          }}
          key={`forecast-${month.forecastId}-month-${month.month}`}
          />
      ))}
    </tr>
  ) : (
    <></>
  );
}

export function AccuracyRows({ actions, ctx, uiState }) {

  const { 
    setCheckColumnWidth, 
    setHierarchyColumnsWidth, 
    setCheckedProducts 
  } = actions || {};

  const { 
    selectedProductIds, 
    forecastAssertivenessMonths = [], 
    selectedHierarchyIndexes, 
    predictionsForAssertivenessAnalysis = [],
    pastPeriodColumns,
    products
  } = ctx || {};

  const { 
    freezeColumns, 
    sumPreviousColumnsWidth, 
    checkedProducts, 
    evaluateLastCategoryColumn, 
    headerHeight, 
    sortRowsBy 
  } = uiState || {};

  const { hierarchy } = useContext(TenantContext);
  
  const productGeneratorRef = useRef(null);
  const [visibleProducts, setVisibleProducts] = useState([]);

  const getProductPredictionFromOfficialPastForecast = useCallback((month, productId) => {
    const officialPastForecast = forecastAssertivenessMonths.find(
      forecast => forecast.month === month.month && forecast.year === month.year
    );

    if (officialPastForecast) {
      return getProductPrediction(month, productId, predictionsForAssertivenessAnalysis);
    } else {
      return {};
    }
  }, [forecastAssertivenessMonths, predictionsForAssertivenessAnalysis])

  const defineLeftCellBorder = useCallback((monthIndex, columnIndex) => {
    return columnIndex === 0 && (monthIndex !== 0);
  }, []);

  const forecastColumnsBorders = useMemo(() => {
    return forecastAssertivenessMonths.map((month, index) => forecastColumns().map((_, i) => defineLeftCellBorder(index, i)));
  }, [forecastAssertivenessMonths, defineLeftCellBorder]);

  const defineHierarchyToDisplay = useCallback(() => {
    return hierarchy.levels.filter((level, index) => selectedHierarchyIndexes.includes(index));
  }, [hierarchy.levels, selectedHierarchyIndexes]);

  const orderedProductIds = useMemo(() => {
    const sorted = [...selectedProductIds];

    if (!sortRowsBy?.columnLabel) return sorted;

    const getPredictionValue = (productId) => {

      const month = predictionsForAssertivenessAnalysis.find(
        (m) => m.month === sortRowsBy.months[0] && m.year === sortRowsBy.year
      );
      

      if (!month) return 0;
      const prediction = getProductPrediction(month, productId, predictionsForAssertivenessAnalysis);

      return prediction?.[sortRowsBy.columnLabel] ?? 0;
    };

    sorted.sort((a, b) => {
      const aValue = getPredictionValue(a);
      const bValue = getPredictionValue(b);

      return sortRowsBy.order === ORDERING_MODES.ASCENDING
        ? aValue - bValue
        : bValue - aValue;
    });

    return sorted;
  }, [selectedProductIds, sortRowsBy]);

  const loadMoreProducts = useCallback(() => {
    const nextBatch = [];
    for (let i = 0; i < 10; i++) {
      const { value, done } = productGeneratorRef.current.next();
      if (done) break;
      nextBatch.push(value);
    }
    if (nextBatch.length > 0) {
      setVisibleProducts(prevProducts => [...prevProducts, ...nextBatch]);
    }
  }, []);

  useEffect(() => {
    productGeneratorRef.current = createProductIdsGenerator(orderedProductIds);
    
    const initialBatch = [];
    for (let i = 0; i < 10; i++) {
      const { value, done } = productGeneratorRef.current.next();
      if (done) break;
      initialBatch.push(value);
    }
    setVisibleProducts(initialBatch);

  }, [orderedProductIds]);

  return (
    <>
      <AccuracySubtotalRow
        actions={{ setCheckedProducts: setCheckedProducts }}
        ctx={{
          selectedProductIds: selectedProductIds,
          defineHierarchyToDisplay: defineHierarchyToDisplay,
          forecastAssertivenessMonths: forecastAssertivenessMonths,
          predictionsForAssertivenessAnalysis: predictionsForAssertivenessAnalysis,
          pastPeriodColumns: pastPeriodColumns
        }}
        uiState={{
          freezeColumns: freezeColumns,
          checkedProducts: checkedProducts,
          sumPreviousColumnsWidth: sumPreviousColumnsWidth,
          evaluateLastCategoryColumn: evaluateLastCategoryColumn,
          headerHeight: headerHeight
        }}/>

      {visibleProducts.map((productId) => (
        <tr key={productId.productId}>
          <CheckCell
            actions={{ 
              setCheckColumnWidth: setCheckColumnWidth,
              setCheckedProducts: setCheckedProducts
            }}
            ctx={{
              productId: productId,
              checkedProducts: checkedProducts,
              selectedHierarchyIndexes: selectedHierarchyIndexes
            }}
            uiState={{
              individualProductCheck: true,
              freezeColumns: freezeColumns
            }}/>

          <HierarchyBodyCells
            ctx={{ 
              productId: productId, 
              selectedHierarchyIndexes: selectedHierarchyIndexes || [],
              allProducts: products
            }}
            uiState={{
              freezeColumns: freezeColumns,
              sumPreviousColumnsWidth: sumPreviousColumnsWidth,
              setHierarchyColumnsWidth: setHierarchyColumnsWidth,
              evaluateLastCategoryColumn: evaluateLastCategoryColumn
            }}/>

          {forecastAssertivenessMonths.map((month, index) => (
            <BodyCells
              ctx={{
                individualProduct: true,
                productIds: selectedProductIds,
                monthIndex: index,
                table: 'accuracy',
                columns: pastPeriodColumns,
                prediction: getProductPredictionFromOfficialPastForecast(month, productId)
              }}
              uiState={{
                forecastColumnsBorders: forecastColumnsBorders[index]
              }}
              key={`forecast-${month.forecastId}-month-${month.month}-product-${productId.productId}`}
              />
          ))}
        </tr>        
      ))}

      {visibleProducts.length < selectedProductIds.length && (
        <tr className="load-more-row">
          <td colSpan={defineHierarchyToDisplay().length} style={{
            textAlign: 'center',
            padding: '1rem',
            border: 'none',
            position: 'sticky',
            left: '2rem',
            bottom: '0',
            backgroundColor: 'white',
            zIndex: 1,
          }}>
            <Button
              variant={'primary'}
              onClick={loadMoreProducts}
              label={`${selectedProductIds.length - visibleProducts.length} restantes`}
              icon={'arrow-down'}
              position={'left'}/>
          </td>
        </tr>
      )}
    </>
  );
};

AccuracySubtotalRow.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
}

AccuracyRows.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
}